<template>
  <Print />
  <CookieModalWrapper />
  <Layout>
    <router-view :key="route.fullPath" />
  </Layout>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useTenant } from '@/core';
import { useHead } from '@unhead/vue';
import useLocalization from '@/services/useLocalization';
import Layout from '@/components/layout/BaseLayout.vue';
import Print from '@/components/print/BasePrint.vue';
import useGenre from '@/services/useGenre';
import useGlobalEvents from '@/services/useGlobalEvents';
import { TenantConfig } from '@/types/interface/TenantConfig.interface';
import CookieModalWrapper from '@/modules/cookie-modal/CookieModalWrapper.vue';
import { useGtm } from '@gtm-support/vue-gtm';
import useGTM from '@/services/useGTM';
import useExercisesDB from '@/services/useExercisesDB';
import useMeta from '@/services/useMeta';

interface AppProps {
  tenantConfig: TenantConfig;
}

const props = defineProps<AppProps>();
const route = useRoute();
const { setGenresByLang } = useGenre();
const { initTenant, tenant } = useTenant<TenantConfig>();
const { locale, getCurrentLocale, setLocale } = useLocalization();
const { registerGlobalEvents } = useGlobalEvents();
const { consent } = useGTM();
const gtm = useGtm();
const { loadMetadata } = useMeta();

setLocale(getCurrentLocale() || props.tenantConfig.lang);
registerGlobalEvents();

onBeforeMount(() => {
  initTenant(props.tenantConfig);
  useHead({
    title: tenant.value.pageTitle || 'Documentation Tool',
  });

  useExercisesDB().initDatabase();
});

onMounted(() => {
  if (props.tenantConfig.analytics?.gtmId) {
    const consentDetails =
      JSON.parse(localStorage.getItem(window.CookieModal.consts.COOKIE_INFORMATION_CONSENT_KEY)) || {};

    consent('default', consentDetails.performance);
    gtm.enable(true);
  }

  loadMetadata();
});

watch(locale, () => setGenresByLang(locale.value.substr(0, 2)));
</script>
